import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export type UserRole = 'user' | 'admin' | 'content_manager' | 'partner';

export interface UserRoleData {
  role: UserRole;
  isAdmin: boolean;
  isStaff: boolean;
  isPartner: boolean;
  canEditCourses: boolean;
  canDeleteCourses: boolean;
}

export const userRoleKeys = {
  all: ['userRole'] as const,
  current: (userId: string) => [...userRoleKeys.all, userId] as const,
};

export function useUserRole(): { data: UserRoleData | undefined; isLoading: boolean } {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: userRoleKeys.current(user?.id ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('role, is_admin')
        .eq('id', user!.id)
        .single();

      if (error) throw error;

      const role = (data?.role as UserRole) || (data?.is_admin ? 'admin' : 'user');
      return {
        role,
        isAdmin: role === 'admin' || data?.is_admin === true,
        isStaff: ['admin', 'content_manager'].includes(role),
        isPartner: role === 'partner',
        canEditCourses: ['admin', 'content_manager', 'partner'].includes(role),
        canDeleteCourses: role === 'admin',
      } satisfies UserRoleData;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!user?.id,
  });

  return { data: query.data, isLoading: query.isLoading };
}
